import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { Link } from 'react-router-dom';
import './viden.css';
import SecondaryButton from '../../components/secondaryButton';

// Komponent til at håndtere forskellige typer af links
const ArtikelLink = ({ artikel, children }) => {
  // Tjek om det er et eksternt link (isExternalLink flag eller starter med http)
  const isExternal = artikel.isExternalLink || 
                    artikel.link.startsWith('http://') || 
                    artikel.link.startsWith('https://');
  
  if (isExternal) {
    return (
      <a 
        href={artikel.link} 
        target="_blank" 
        rel="noopener noreferrer"
        className="viden-artikel-link"
      >
        {children}
      </a>
    );
  } else {
    // For interne links bruger vi React Router's Link
    return (
      <Link to={artikel.link} className="viden-artikel-link">
        {children}
      </Link>
    );
  }
};

const Viden = () => {
  const [activeTag, setActiveTag] = useState('alle');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data states
  const [videnData, setVidenData] = useState({
    heroImage: '/media/hero/MBT-boger.avif',
    introText: 'Velkommen til vores videns afsnit, her kan du kan orientere dig i vores egne bogudgivelser, boganmeldelser af gode børnebøger og fagbøger samt videns artikler om forskellige psykologfaglige emner som optager os hver især.'
  });
  const [tags, setTags] = useState([
    { id: 'alle', name: 'Alle' },
    { id: 'bog', name: 'Bog' },
    { id: 'artikel', name: 'Artikel' },
    { id: 'podcast', name: 'Podcast' }
  ]);
  const [artikler, setArtikler] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const videnDoc = await getDoc(doc(db, 'content', 'viden'));
        
        if (videnDoc.exists()) {
          const data = videnDoc.data();
          
          // Opdater videnData
          if (data.heroImage || data.introText) {
            setVidenData({
              heroImage: data.heroImage || videnData.heroImage,
              introText: data.introText || videnData.introText
            });
          }
          
          // Opdater tags, men behold kun de standard tags vi har defineret
          if (data.tags && data.tags.length > 0) {
            const standardTagIds = ['alle', 'bog', 'artikel', 'podcast'];
            const filteredTags = data.tags.filter(tag => standardTagIds.includes(tag.id));
            
            setTags([
              { id: 'alle', name: 'Alle' },
              ...filteredTags
            ]);
          }
          
          // Opdater artikler
          if (data.artikler && data.artikler.length > 0) {
            // Sorter artikler baseret på ID (nyeste først)
            const sortedArtikler = data.artikler.sort((a, b) => {
              // Hent timestamp fra ID (artikel-{timestamp})
              const timestampA = parseInt(a.id.split('-')[1]);
              const timestampB = parseInt(b.id.split('-')[1]);
              return timestampB - timestampA; // Sorter i faldende rækkefølge
            });
            setArtikler(sortedArtikler);
          }
        }
      } catch (err) {
        console.error('Fejl ved indlæsning af viden data:', err);
        setError('Der opstod en fejl ved indlæsning af data. Prøv venligst igen senere.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const filteredArtikler = activeTag === 'alle' 
    ? artikler 
    : artikler.filter(artikel => artikel.tags && artikel.tags.includes(activeTag));

  if (loading) {
    return (
      <div className="viden-side">
        <div className="viden-container loading-container">
          <div className="loading-spinner"></div>
          <p>Indlæser indhold...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="viden-side">
        <div className="viden-container error-container">
          <h2>Ups, noget gik galt</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="viden-side">
      <div className="viden-container">
        <h1>Viden</h1>
        
        <div className="viden-hero">
          <div className="viden-hero-content">
            <div className="viden-hero-billed">
              <img src={videnData.heroImage} alt="Viden hero billede" />
            </div>
            <p>{videnData.introText}</p>
          </div>
        </div>

        <div className="viden-section">
          <div className="viden-filters">
            <h3>Vælg hvad du vil se</h3>
            <div className="viden-tags">
              {tags.map(tag => (
                <button 
                  key={tag.id}
                  className={`viden-tag ${activeTag === tag.id ? 'viden-tag--active' : ''}`}
                  onClick={() => setActiveTag(tag.id)}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="viden-områder">
          {filteredArtikler.length > 0 ? (
            filteredArtikler.map(artikel => (
              <article key={artikel.id} className="viden-kort">
                <div className="viden-venstre">
                  <img src={artikel.image} alt={artikel.title} />
                </div>
                <div className="viden-højre">
                  <h2>{artikel.title}</h2>
                  <p>{artikel.description}</p>
                  <div className="viden-artikel-tags">
                    {artikel.tags && artikel.tags.map(tagId => (
                      <span key={tagId} className="viden-artikel-tag">
                        {tags.find(t => t.id === tagId)?.name}
                      </span>
                    ))}
                  </div>
                  <ArtikelLink artikel={artikel}>
                    <SecondaryButton>
                      {artikel.isExternalLink ? 'Besøg link' : 'Læs mere'}
                    </SecondaryButton>
                  </ArtikelLink>
                </div>
              </article>
            ))
          ) : (
            <div className="no-artikler">
              <p>Ingen artikler fundet for denne kategori.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Viden;
